import axios from "axios";
import { forgetActiveEngagement } from "@/lib/activeEngagement";   // Step 11d-3 · find 1: the per-tab engagement
import * as _sc from "@/lib/savedCopy";   // Batch 29 · M69
import { toast } from "sonner";
import { translations, t as tt } from "@/lib/i18n";
import { handleTypedAmount } from "@/lib/typedAmountConfirm";   // STEP 11d · A6
import { typedKeptMessage } from "@/lib/typedAmountKept";      // Step 12 add-on G6.1: one helper for Cancel

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export { BACKEND_URL };
export const API_URL = `${BACKEND_URL}/api`;

// Default axios timeout is 0 (no timeout) — Kubernetes ingress usually cuts
// the request at ~60–120 s. For upload flows we want a generous window so
// the initial POST for a ~500k-row file survives while the file body streams
// in, then the request completes as soon as the backend queues the job.
export const UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;   // 15 minutes

const api = axios.create({ baseURL: API_URL });

// FastAPI validation errors (422) come back as `detail: [{type,loc,msg,input,url},…]`.
// Rendering that array directly in JSX crashes React ("Objects are not valid as a
// React child"). Every place that displays a server error string should route
// through `toErrMsg` first.
//
// Slice 4 — if the response body carries a machine-readable `error_code`,
// prefer its i18n translation (`errors.<CODE>`) over the raw server
// `detail` string. Falls back to the raw `detail` if the code has no
// mapping yet, so nothing breaks when new codes land before their
// translations do.
export function toErrMsg(err, fallback = "Something went wrong") {
  const kept = typedKeptMessage(err);                     // STEP 11d · A6: the user kept the typed W9 amount (G6.1: one helper)
  if (kept) return kept;
  const data = err?.response?.data;
  const code = data?.error_code;
  const lang = (typeof localStorage !== "undefined"
                  && localStorage.getItem("af_lang")) || "en";
  // Slice 4 EN-preservation fix: for the default server language (en) the
  // raw `detail` string is already the richest form of the message — it
  // carries every interpolated value the caller had at raise time (grade
  // names, IDs, counts, etc.). Only *non*-en users benefit from the
  // code→i18n indirection. If we mapped en → generic template we would
  // silently drop information the raise site went out of its way to
  // include. Rule: en always uses raw detail; non-en prefers translated,
  // falls back to raw detail if the code is unmapped.
  if (code && typeof code === "string" && lang !== "en") {
    const dict = translations[lang] || translations.en;
    const translated = tt(dict, `errors.${code}`);
    if (translated && translated !== `errors.${code}`) {
      return translated;
    }
  }
  const raw = data?.detail;
  if (raw == null) return err?.message || fallback;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw)) {
    return raw.map((r) => (typeof r === "string" ? r
                            : (r?.msg || r?.message || JSON.stringify(r)))).join(" · ");
  }
  if (typeof raw === "object") return raw.msg || raw.message || JSON.stringify(raw);
  return String(raw);
}

// ---------- Short-lived download-scoped tokens (audit fix H2) ----------
// Previously we embedded the raw 7-day session JWT in <img>/<a> URLs
// (leaked to server logs, browser history, Referer). Now the backend
// mints a doc-bound, 5-minute token via POST /documents/:id/download-token
// which is the ONLY thing accepted on ?token= for /documents/file/*.
// We cache tokens per docId (with a 30s safety buffer) so a page with
// many attachments doesn't hammer the endpoint.
const _downloadTokenCache = {};

export async function getDownloadToken(docId) {
  const cached = _downloadTokenCache[docId];
  const now = Date.now();
  if (cached && cached.expiresAt > now + 30_000) return cached.token;
  const r = await api.post(`/documents/${docId}/download-token`, null,
                            { _silent: true });
  _downloadTokenCache[docId] = {
    token: r.data.token,
    expiresAt: now + (r.data.expires_in || 300) * 1000,
  };
  return r.data.token;
}

// Build a full download URL with a fresh scoped token embedded.
// Use for direct navigation (window.open) or inside <img src>.
export async function getDownloadUrl(docId, { mode = "preview" } = {}) {
  const token = await getDownloadToken(docId);
  return `${API_URL}/documents/file/${docId}?token=${encodeURIComponent(token)}&mode=${mode}`;
}



let _refusedToastAt = 0;
api.interceptors.request.use((config) => {
  // Batch 31 · item 17 (M85): the screen shows its saved version — a write is refused here, before it leaves the browser
  const lang = (typeof document !== "undefined" && document.documentElement.getAttribute("lang")) === "ar" ? "ar" : "en";
  const refused = _sc.refusedWrite(config, lang);
  if (refused) {
    if (Date.now() - _refusedToastAt > 3000) { _refusedToastAt = Date.now(); toast.error(refused.message); }
    return Promise.reject(refused);
  }
  const t = localStorage.getItem("af_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Silence 404s on engagement-scoped GETs (stale engagement id in localStorage) —
// return an empty response instead of rejecting, so page loads don't crash.
// Every path segment that takes an :eng_id in the URL MUST be listed here,
// otherwise a stale af_eng (lib/activeEngagement.js) triggers a global toast on every
// mount of a page that fires that call (Platform page saw this via the
// Layout /forms/{eng}/progress poller). Order is not significant.
//
// NOTE — "risk" is INTENTIONALLY OMITTED. Its endpoints return
// structured objects (e.g. /risk/{id}/fraud returns
// `{ groups, notes, population_size }`), not lists — silently
// coalescing a 404 to `{ data: [] }` used to feed an array into
// object-shaped state, blanking the Fraud Risk and Cut-off tabs
// without a toast. Real 404s on /risk/ endpoints now surface as
// normal errors and callers must shape-guard their state.
const ENG_SCOPED = [
  /\/(coa|gl|analytics|samples|vouching|documents|discrepancies|ifrs|assistant|audit-trail|forms|fs|time|search|attachments|engagements|billing)\/[^/]+/,
];

// Global step-up handler — registered by <StepUpDialog /> at app mount.
let stepUpHandler = null;
export const registerStepUpHandler = (fn) => { stepUpHandler = fn; };

// Global AI-allocation-exhausted handler — registered by
// <AiExhaustedGateModal /> at app mount. The interceptor below fires
// this every time a 402 "ai_allocation_exhausted" is seen — every hit,
// every callsite, no once-per-session gating (product decision).
let aiExhaustedHandler = null;
export const registerAiExhaustedHandler = (fn) => { aiExhaustedHandler = fn; };

// Batch 12 · L4 (D7): every saved change (POST / PUT / PATCH / DELETE that succeeded, sign-off actions aside) tells the
// sign-off bars on screen to re-read their flags, so "Changed after sign-off" shows as soon as the change is saved —
// not only after the item is closed and reopened. The server decides what counts as a change (signoffs.note_change).
export const ITEM_SAVED_EVENT = "af-item-saved";
export const ENGAGEMENT_STALE_EVENT = "af-engagement-stale";
const _announceSave = (r) => {
  try {
    const c = (r && r.config) || {};
    const m = (c.method || "get").toLowerCase();
    if (m !== "get" && m !== "head" && m !== "options" && !/\/signoffs(\/|\?|$)/.test(c.url || "")) {
      window.dispatchEvent(new CustomEvent(ITEM_SAVED_EVENT, { detail: { url: c.url || "", method: m } }));
    }
  } catch { /* never block a response */ }
  return r;
};

// Batch 29 · M69: a screen read answered from its last-good copy (or with "never saved") is reported to the safety net
// (components/SavedCopyFrame.jsx); every GET a page makes is noted so a page that fails to render can ask for its copies.
const _savedCopyCheck = (r) => {
  try {
    const c = (r && r.config) || {};
    if ((c.method || "get").toLowerCase() === "get") {
      _sc.noteRead(_sc.apiPathOf(c));
      const h = (r && r.headers) || {};
      if (h["x-azex-fallback"] === "1") {
        _sc.announce({ kind: "copy", savedAt: h["x-azex-saved-at"] || "", savedBy: h["x-azex-saved-by"] || "", page: window.location.pathname });
      }
    }
  } catch { /* never block a response */ }
  return r;
};

api.interceptors.response.use(
  (r) => _announceSave(_savedCopyCheck(r)),
  async (err) => {
    if (err && err._savedCopyRefused) return Promise.reject(err);   // Batch 31 · M85: already told (one toast)
    const status = err.response && err.response.status;
    const cfg = err.config || {};
    const url = cfg.url || "";
    const method = (cfg.method || "get").toLowerCase();
    if (status === 503 && err.response.data && err.response.data.error_code === _sc.NO_COPY_CODE) {   // Batch 29 · M69
      _sc.announce({ kind: "never", page: window.location.pathname });
    }

    // STEP 11d · A6: a typed W9 amount is replaced by the lines only after the user confirms (then the request is sent again)
    if (status === 409 && err.response?.data?.error_code === "MISSTATEMENT_AMOUNT_TYPED") {
      const again = await handleTypedAmount(err, api);
      if (again) return again;
      return Promise.reject(err);                // Cancel: no toast here; toErrMsg says the typed amount is kept
    }
    if (status === 404) {     // Batch 27 · Add-on 3 · C (M41): an unknown route may mean a newer server — check the build
      try { window.dispatchEvent(new CustomEvent("af-api-404", { detail: { url } })); } catch { /* no window */ }
    }
    if (status === 401) {
      localStorage.removeItem("af_token");
      forgetActiveEngagement({ all: true });   // Step 11d-3 · find 1: this tab and the new-tab starting value
      if (!window.location.pathname.endsWith("/login")) {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }

    // Step-up 2FA required. Open the dialog, wait for verification, retry.
    if (status === 428 && err.response?.data?.detail === "STEP_UP_REQUIRED"
        && stepUpHandler && !cfg._stepUpRetried) {
      try {
        await stepUpHandler();
        cfg._stepUpRetried = true;
        return api.request(cfg);
      } catch {
        return Promise.reject(err);
      }
    }

    // AI allocation gate: 402 { detail: { code: "ai_allocation_exhausted", ... } }
    // Open the global modal and suppress the default toast — we don't
    // want a duplicate "The key is consumed." toast underneath the modal.
    if (status === 402
        && err.response?.data?.detail?.code === "ai_allocation_exhausted"
        && aiExhaustedHandler) {
      try { aiExhaustedHandler(err.response.data.detail); } catch { /* ignore */ }
      return Promise.reject(err);
    }

    // Batch 12 · L5 (F-92): the remembered engagement belongs to another firm (403 ENGAGEMENT_ACCESS_DENIED) or was
    // deleted (404 ENGAGEMENT_NOT_FOUND) — forget it and tell EngagementContext, which clears the selection and re-reads
    // the firm's engagement list (R51: never switch to another engagement by itself; the pages ask the user to choose).
    const code = err.response?.data?.error_code;
    if ((code === "ENGAGEMENT_NOT_FOUND" || code === "ENGAGEMENT_ACCESS_DENIED") && ENG_SCOPED.some((rx) => rx.test(url))) {
      forgetActiveEngagement();   // Step 11d-3 · find 1: this tab's engagement (and the starting value when it is the same)
      try { window.dispatchEvent(new CustomEvent(ENGAGEMENT_STALE_EVENT, { detail: { url, status } })); } catch { /* ignore */ }
      if (method === "get") return Promise.resolve({ data: [], _stale: true });
    }

    // Stale engagement id → GET returns 404. Clear cache and resolve empty.
    if (status === 404 && method === "get" && ENG_SCOPED.some((rx) => rx.test(url))) {
      forgetActiveEngagement();
      return Promise.resolve({ data: [], _stale: true });
    }

    // Any other error surfaces as a friendly toast (avoids raw stack traces).
    // Uses `toErrMsg` so FastAPI 422 arrays never render as "[object Object]".
    // Callers that show their own toast (via their own `catch`) should pass
    // `_silent: true` on the request config to suppress this global toast and
    // avoid duplicate notifications.
    // Batch 29 · M65: a worksheet send of an item already in Misstatements opens the "Already in Misstatements – link" dialog
    // (offerLink) — no toast saying the same thing under it
    const dupSend = code === "MISSTATEMENT_DUPLICATE" && /send-to-misstatements|send-from-finding/.test(url);
    if (status && status >= 400 && !cfg._silent && !dupSend) {
      toast.error(toErrMsg(err, "Request failed").slice(0, 300));
    }
    return Promise.reject(err);
  },
);

export default api;
